"use client";

import { c as _c } from "react/compiler-runtime";
import { toast } from "sonner";
import { useAnalytics } from "@/lib/analytics/client";
import type { OAuthProviderConfig } from "@/lib/oauth";
import { signIn } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
interface OAuthButtonProps {
  /** Provider configuration */
  provider: OAuthProviderConfig;
  /** URL to redirect to after successful sign-in */
  callbackURL?: string;
  /** Additional class names */
  className?: string;
  /** Whether this button is loading */
  isLoading?: boolean;
  /** Whether any OAuth button is loading (disables all buttons) */
  isAnyLoading?: boolean;
  /** Callback when loading state changes */
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Generic OAuth sign-in button that works with any configured provider.
 *
 * Usage:
 * ```tsx
 * import { getEnabledProviders } from "@/lib/constants/oauth-providers";
 *
 * // Render buttons for all enabled providers
 * {getEnabledProviders().map((provider) => (
 *   <OAuthButton key={provider.id} provider={provider} />
 * ))}
 * ```
 */
export function OAuthButton(t0) {
  const $ = _c(17);
  const {
    provider,
    callbackURL,
    className,
    isLoading: t1,
    isAnyLoading: t2,
    onLoadingChange
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isAnyLoading = t2 === undefined ? false : t2;
  const analytics = useAnalytics();
  const Icon = provider.icon;
  let t3;
  if ($[0] !== analytics || $[1] !== callbackURL || $[2] !== onLoadingChange || $[3] !== provider.id || $[4] !== provider.name) {
    t3 = async () => {
      onLoadingChange?.(true);
      analytics.track("sign_in_clicked", {
        provider: provider.id
      });
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          onLoadingChange?.(false);
          document.removeEventListener("visibilitychange", handleVisibilityChange);
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      ;
      try {
        await signIn.social({
          provider: provider.id,
          callbackURL,
          errorCallbackURL: "/login"
        });
      } catch (t4) {
        const err = t4;
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        onLoadingChange?.(false);
        analytics.trackException(err, {
          provider: provider.id,
          action: "sign_in"
        });
        toast.error(`Failed to sign in with ${provider.name}.`, {
          description: "Please try again or choose a different provider."
        });
      }
    };
    $[0] = analytics;
    $[1] = callbackURL;
    $[2] = onLoadingChange;
    $[3] = provider.id;
    $[4] = provider.name;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleSignIn = t3;
  let t4;
  if ($[6] !== className) {
    t4 = cn("w-full gap-3", className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Icon || $[9] !== isLoading) {
    t5 = isLoading ? <Spinner className="size-5" /> : <Icon className="size-5" />;
    $[8] = Icon;
    $[9] = isLoading;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== handleSignIn || $[12] !== isAnyLoading || $[13] !== provider.name || $[14] !== t4 || $[15] !== t5) {
    t6 = <Button size="lg" variant="outline" className={t4} onClick={handleSignIn} disabled={isAnyLoading}>{t5}Continue with {provider.name}</Button>;
    $[11] = handleSignIn;
    $[12] = isAnyLoading;
    $[13] = provider.name;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}